
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, Search } from 'lucide-react';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  labelIcon?: React.ReactNode;
  label: string;
  id?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  address: {
    road?: string;
    house_number?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
  };
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ 
  value, 
  onChange, 
  placeholder, 
  labelIcon, 
  label,
  id
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timeOutId = setTimeout(async () => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }
      
      if (!isOpen && query === value) return;

      setIsLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5&countrycodes=de,at,ch&email=r.ehlers@gefas-mbh.de&accept-language=de`
        );
        
        if (!response.ok) throw new Error(`API Error: ${response.status}`);

        const data = await response.json();
        setSuggestions(data);
        if (data.length > 0) setIsOpen(true);
      } catch (error) {
        console.error("Address fetch error:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeOutId);
  }, [query, isOpen, value]);

  const handleSelect = (item: NominatimResult) => {
    const addr = item.address;
    let formatted = item.display_name;

    if (addr.road || addr.city || addr.town || addr.village) {
      const street = `${addr.road || ''} ${addr.house_number || ''}`.trim();
      const city = addr.city || addr.town || addr.village || '';
      const zip = addr.postcode || '';
      
      const part1 = street ? `${street}, ` : '';
      const part2 = zip && city ? `${zip} ${city}` : city;
      
      if (part1 || part2) {
        formatted = `${part1}${part2}`.trim();
        if (formatted.endsWith(',')) formatted = formatted.slice(0, -1);
      }
    }

    setQuery(formatted);
    onChange(formatted);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="space-y-1 relative" ref={wrapperRef}>
      <label className="text-xs font-medium text-slate-500 uppercase flex items-center gap-1">
        {labelIcon} {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 3 && setIsOpen(true)}
          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-8"
          placeholder={placeholder}
        />
        <div className="absolute right-3 top-3 text-slate-400">
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> : <Search className="w-4 h-4 opacity-50" />}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full bg-white border border-slate-200 rounded-xl shadow-xl mt-1 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
          {suggestions.map((item) => (
            <li
              key={item.place_id}
              onClick={() => handleSelect(item)}
              className="px-3 py-2.5 hover:bg-blue-50 cursor-pointer text-sm text-slate-700 border-b border-slate-100 last:border-0 flex items-start gap-2 transition-colors"
            >
              <MapPin className="w-4 h-4 mt-0.5 text-blue-400 flex-shrink-0" />
              <span className="leading-snug">{item.display_name}</span>
            </li>
          ))}
          <li className="px-3 py-1.5 bg-slate-50 text-[11px] text-slate-400 text-right border-t border-slate-100">
            Daten von OpenStreetMap
          </li>
        </ul>
      )}
    </div>
  );
};
